/**
 * GLO Color Swatch × Spark theme gallery compatibility patch.
 *
 * Goal: stop the product gallery from showing all 38 colour images at once.
 * When a colour is selected, show only that colour's own image(s) and hide the rest.
 *
 * ── How it works ─────────────────────────────────────────────────────────
 * GLO Color Swatch still renders the swatches and still changes the selected
 * variant (URL / variant:changed). This script only listens for that change
 * and filters the Spark gallery. It does NOT read GLO's internal data.
 *
 * Source of truth is Shopify-native:
 *   1. variant.featured_media.id  → the colour's main image (matches the DOM).
 *   2. media that SHARE that image's alt text → the colour's extra shots.
 * The store tags each colour's photos with the colour in the alt text (e.g.
 * both Jet Black images use alt "#1- Jet Black"). So #1 Jet Black shows its 2
 * images, and colours with only a swatch show 1 — no generic lifestyle photo
 * is forced onto every colour.
 *
 * ── Spark DOM ────────────────────────────────────────────────────────────
 *   .product__media-slide      → child .media[data-media-id]   (mobile adds "-carousel")
 *   .product__media-item[data-media-item-id]
 *   .product-thumbnails__item  → child button[data-thumbnail-id]
 *
 * ── Config (set on window) ───────────────────────────────────────────────
 *   window._gloGalleryDebug      = true   → verbose logging
 *   window._gloGalleryAlwaysShow = [ids]  → media ids visible on every colour
 *   window._gloGalleryInspect()           → console diagnostic
 */
(function () {
  'use strict';

  // ── State ────────────────────────────────────────────────────────────────
  var productData   = null;
  var lastVariantId = null;
  var lastMediaIds  = null;   // last applied filter, for re-application after re-render
  var initialized   = false;
  var urlTimer      = null;
  var reapplyTimer  = null;

  function log() {
    if (window._gloGalleryDebug) {
      console.log.apply(console, ['[glo-gallery]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // ── Product data ─────────────────────────────────────────────────────────
  function getHandle() {
    var link = document.querySelector('link[rel="canonical"]');
    var m    = link && link.href.match(/\/products\/([^?#/]+)/);
    return m ? m[1] : null;
  }
  function loadProduct(callback) {
    var handle = getHandle();
    if (!handle) { return callback(null); }
    var xhr = new XMLHttpRequest();
    xhr.open('GET', '/products/' + handle + '.js');
    xhr.onload = function () {
      try { productData = JSON.parse(xhr.responseText); } catch (e) { /* ignore */ }
      callback(productData);
    };
    xhr.onerror = function () { callback(null); };
    xhr.send();
  }

  // ── DOM id helpers ───────────────────────────────────────────────────────
  function normalizeId(raw) {            // Spark appends "-carousel" on mobile clones
    return raw ? String(raw).replace(/-carousel$/, '') : '';
  }
  function getSlideId(slideEl) {         // data-media-id is on the child .media
    var inner = slideEl.querySelector('[data-media-id]');
    return inner ? normalizeId(inner.dataset.mediaId) : '';
  }
  function getMediaItemId(el) {
    return el.dataset.mediaItemId ? normalizeId(el.dataset.mediaItemId) : '';
  }
  function getThumbId(liEl) {
    var btn = liEl.querySelector('[data-thumbnail-id]');
    return btn ? String(btn.dataset.thumbnailId) : '';
  }
  function buildDomIdSet() {             // every media id present in the DOM
    var set = Object.create(null);
    document.querySelectorAll('.product__media-slide').forEach(function (el) {
      var id = getSlideId(el); if (id) set[id] = true;
    });
    document.querySelectorAll('.product__media-item').forEach(function (el) {
      var id = getMediaItemId(el); if (id) set[id] = true;
    });
    document.querySelectorAll('.product-thumbnails__item').forEach(function (el) {
      var id = getThumbId(el); if (id) set[id] = true;
    });
    return set;
  }

  // ── Media ID resolution (native Shopify data) ────────────────────────────

  // The variant's own colour image (id + alt text).
  function variantFeaturedMedia(variantId) {
    var vs = (productData && productData.variants) || [];
    for (var i = 0; i < vs.length; i++) {
      if (vs[i].id === variantId && vs[i].featured_media && vs[i].featured_media.id) {
        return { id: String(vs[i].featured_media.id), alt: (vs[i].featured_media.alt || '') };
      }
    }
    return null;
  }

  function normAlt(s) { return (s || '').trim().toLowerCase(); }

  // Other media that belong to the same colour — they share the featured image's
  // alt text (e.g. both Jet Black photos have alt "#1- Jet Black"). This is how we
  // get "2 images for Jet Black, 1 for the rest" natively, without GLO's data.
  function sameAltMediaIds(alt, excludeId, domIdSet) {
    var key = normAlt(alt);
    if (!key) return [];                 // empty/lifestyle alt → don't group
    var out = [];
    ((productData && productData.media) || []).forEach(function (m) {
      var id = String(m.id);
      if (id !== excludeId && normAlt(m.alt) === key && domIdSet[id]) out.push(id);
    });
    return out;
  }

  // Optional always-visible media ids: window._gloGalleryAlwaysShow = [ids]
  function alwaysShowIds(domIdSet) {
    if (!Array.isArray(window._gloGalleryAlwaysShow)) return [];
    return window._gloGalleryAlwaysShow.map(String).filter(function (id) { return domIdSet[id]; });
  }

  /**
   * Media ids that should be visible for this variant, or null = show everything.
   *   1. variant.featured_media.id  + media sharing its alt (the colour's own shots)
   *   2. media[].variant_ids if assigned in Shopify admin
   * plus any window._gloGalleryAlwaysShow ids.
   */
  function resolveMediaIds(variantId) {
    var domIdSet = buildDomIdSet();
    var ids = null;

    var feat = variantFeaturedMedia(variantId);
    if (feat && domIdSet[feat.id]) {
      ids = [feat.id].concat(sameAltMediaIds(feat.alt, feat.id, domIdSet));
      log('resolved via featured_media + same-alt', ids);
    } else {
      var assigned = [];
      ((productData && productData.media) || []).forEach(function (m) {
        if (m.variant_ids && m.variant_ids.indexOf(variantId) !== -1 && domIdSet[String(m.id)]) {
          assigned.push(String(m.id));
        }
      });
      if (assigned.length) { ids = assigned; log('resolved via media[].variant_ids', ids); }
    }

    if (!ids) {
      log('no variant-specific image data — showing all. Run _gloGalleryInspect() to diagnose.');
      return null;
    }
    alwaysShowIds(domIdSet).forEach(function (id) { if (ids.indexOf(id) === -1) ids.push(id); });
    return ids;
  }

  // ── Gallery DOM filtering ────────────────────────────────────────────────
  function applyFilter(mediaIds) {
    if (!mediaIds) { showAll(); return; }
    var ids = mediaIds.map(String);
    log('filtering gallery to', ids);

    var slides = document.querySelectorAll('.product__media-slide');
    var withId = 0, visible = 0;
    slides.forEach(function (el) {
      var id = getSlideId(el);
      if (!id) return;
      withId++;
      var show = ids.indexOf(id) !== -1;
      el.style.display = show ? '' : 'none';
      if (show) visible++;
    });
    // Safety: if every slide would be hidden (id mismatch), bail rather than blank.
    if (withId > 0 && visible === 0) {
      log('WARNING: all slides would be hidden — aborting filter.');
      showAll();
      return;
    }

    document.querySelectorAll('.product__media-item').forEach(function (el) {
      var id = getMediaItemId(el);
      if (id) el.style.display = ids.indexOf(id) !== -1 ? '' : 'none';
    });
    document.querySelectorAll('.product-thumbnails__item').forEach(function (el) {
      var id = getThumbId(el);
      if (id) el.style.display = ids.indexOf(id) !== -1 ? '' : 'none';
    });
  }

  function showAll() {
    ['.product__media-slide', '.product__media-item', '.product-thumbnails__item'].forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) { el.style.display = ''; });
    });
  }

  // ── Diagnostic ───────────────────────────────────────────────────────────
  window._gloGalleryInspect = function () {
    console.group('[glo-gallery] inspect');
    var domIds = Object.keys(buildDomIdSet());
    console.log('DOM media ids (' + domIds.length + '):', domIds);
    if (!productData) { console.warn('product JSON not loaded yet'); console.groupEnd(); return; }

    var withFeatured = 0, matched = 0;
    (productData.variants || []).forEach(function (v) {
      if (!v.featured_media) return;
      withFeatured++;
      if (domIds.indexOf(String(v.featured_media.id)) !== -1) matched++;
    });
    console.log('variants with featured_media:', withFeatured + '/' + (productData.variants || []).length);
    console.log('featured_media ids that exist in the DOM:', matched + '/' + withFeatured,
      matched === withFeatured ? '✓ native mapping works' : '✗ some ids missing from DOM');

    // How many colours have >1 image sharing their featured alt (i.e. a 2nd shot)?
    var grouped = [];
    (productData.variants || []).forEach(function (v) {
      if (!v.featured_media) return;
      var n = 1 + sameAltMediaIds(v.featured_media.alt, String(v.featured_media.id), buildDomIdSet()).length;
      if (n > 1) grouped.push(v.public_title + ' (' + n + ')');
    });
    console.log('colours with >1 grouped image:', grouped.length ? grouped : 'none');
    console.log('current resolve →', resolveMediaIds(getInitialVariantId()));
    console.groupEnd();
  };

  // ── Variant change wiring ────────────────────────────────────────────────
  function onVariantChange(variantId) {
    variantId = parseInt(variantId, 10);
    if (!variantId || variantId === lastVariantId) return;
    lastVariantId = variantId;
    log('variant changed to', variantId);
    lastMediaIds = resolveMediaIds(variantId);
    applyFilter(lastMediaIds);
  }

  function getVariantFromUrl() {
    return parseInt(new URLSearchParams(location.search).get('variant'), 10) || null;
  }
  // Initial variant when the URL has no ?variant= (first load).
  function getInitialVariantId() {
    var fromUrl = getVariantFromUrl();
    if (fromUrl) return fromUrl;
    var el = document.querySelector('[data-current-variant-id]')
          || document.querySelector('[data-section-type="product"][data-current-product-id]');
    var attr = el && (el.getAttribute('data-current-variant-id') || el.getAttribute('data-current-product-id'));
    var id = parseInt(attr, 10);
    if (id) return id;
    var sel = document.querySelector('[data-variant-select], #variant-selector, select[name="id"]');
    return sel ? (parseInt(sel.value, 10) || null) : null;
  }

  function scheduleUrlCheck() {
    clearTimeout(urlTimer);
    urlTimer = setTimeout(function () {
      var id = getVariantFromUrl();
      if (id) onVariantChange(id);
    }, 150);
  }

  function setupListeners() {
    // Spark / Shopify variant change events.
    document.addEventListener('variant:changed', function (e) {
      var v = e.detail && e.detail.variant;
      if (v) onVariantChange(v.id);
    });
    document.addEventListener('variantChange', function (e) {
      var id = e.detail && (e.detail.variantId || (e.detail.variant && e.detail.variant.id));
      if (id) onVariantChange(id);
    });
    // GLO updates the URL on swatch click — catch pushState + popstate.
    var _push = history.pushState.bind(history);
    history.pushState = function (state, title, url) { _push(state, title, url); scheduleUrlCheck(); };
    window.addEventListener('popstate', scheduleUrlCheck);
    // Backup: catch GLO swatch clicks directly.
    document.addEventListener('click', function (e) {
      var swatch = e.target.closest &&
        e.target.closest('.gcw-variant-img-swatch-with-price, .globo-swatch-item, [data-swatch-value]');
      if (swatch) setTimeout(scheduleUrlCheck, 300);
    }, true);
  }

  // Spark re-renders the product media section on variant change, wiping our
  // inline display styles. Re-apply the last filter when the gallery DOM changes.
  function setupGalleryReapply() {
    var container = document.querySelector('.product__media-container')
                 || document.querySelector('[data-section-type="product"]');
    if (!container) return;
    var obs = new MutationObserver(function () {
      if (!lastMediaIds) return;
      clearTimeout(reapplyTimer);
      reapplyTimer = setTimeout(function () { applyFilter(lastMediaIds); }, 50);
    });
    // childList only — applyFilter changes inline styles (attributes), not the
    // node tree, so it won't retrigger this observer and loop.
    obs.observe(container, { childList: true, subtree: true });
  }

  // ── Bootstrap ────────────────────────────────────────────────────────────
  function init() {
    if (initialized) return;
    initialized = true;
    if (!document.querySelector('[data-section-type="product"]')) return;
    log('initialising');
    loadProduct(function () {
      setupListeners();
      setupGalleryReapply();
      var initial = getInitialVariantId();
      if (initial) onVariantChange(initial);
    });
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
}());
